import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Checkbox, FormControlLabel, Chip,
    Grid, Typography, Box
} from '@mui/material';

export default function LoanSelectorDialog({
    open,
    onClose,
    onConfirm,
    onSearch,
    loanResults,
    selectedLoans,
    setSelectedLoans,
    defaultMilestone,
    setDefaultMilestone,
    ignoreDuplicates,
    setIgnoreDuplicates
}) {
    const [loanNumberInput, setLoanNumberInput] = useState('');
    const [borrowerNameInput, setBorrowerNameInput] = useState('');

    const handleSearch = () => {
        const loanNumbers = loanNumberInput
            .split(',')
            .map(s => s.trim())
            .filter(s => s);

        onSearch({ loanNumbers, borrowerName: borrowerNameInput });
    };

    const handleSelectLoan = (loan) => {
        if (!selectedLoans.some(l => l.loanId === loan.loanId)) {
            setSelectedLoans([...selectedLoans, loan]);
        }
    };

    const handleRemoveLoan = (loanId) => {
        setSelectedLoans(selectedLoans.filter(l => l.loanId !== loanId));
    };

    const handleConfirm = () => {
        onConfirm();
        setLoanNumberInput('');
        setBorrowerNameInput('');
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { maxHeight: '90vh' }  // limits full dialog height
            }}
        >
            <DialogTitle>Select Loans to Upload To</DialogTitle>
            <DialogContent
                dividers
                sx={{
                    maxHeight: '80vh',         // limits total vertical height
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label="Loan Numbers (comma-separated)"
                            value={loanNumberInput}
                            onChange={(e) => setLoanNumberInput(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Borrower Name"
                            value={borrowerNameInput}
                            onChange={(e) => setBorrowerNameInput(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Button onClick={handleSearch} variant="contained">Search</Button>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle1">Search Results:</Typography>
                        <Box
                            sx={{
                                maxHeight: '200px',       // or whatever height you prefer
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                pr: 1                      // optional: padding for scrollbar spacing
                            }}
                        >
                        {loanResults.length > 0 ? (
                            loanResults.map((loan) => (
                                <Box key={loan.loanNumber} display="flex" justifyContent="space-between" alignItems="center" my={1}>
                                    <Typography noWrap={false} sx={{ overflowWrap: 'anywhere' }}>
                                        {loan.fields?.["Loan.LoanNumber"]} - {loan.fields?.["Loan.BorrowerName"]}
                                    </Typography>
                                    <Button variant="outlined" onClick={() => handleSelectLoan(loan)}>Select</Button>
                                </Box>
                            ))
                        ) : (
                            <Typography color="text.secondary">No loans found.</Typography>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle1">Selected Loans:</Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {selectedLoans.map((loan) => (
                                <Chip
                                    key={loan.loanId}
                                    label={loan.fields["Loan.LoanNumber"]}
                                    onDelete={() => handleRemoveLoan(loan.loanId)}
                                    color="primary"
                                />
                            ))}
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Preferred Document Destination Milestone"
                            value={defaultMilestone}
                            onChange={(e) => setDefaultMilestone(e.target.value)}
                            helperText="The system will first try to match a document with this milestone. If no match is found, it will try to match a document with the current loan milestone. If neither is found, the document will be created under the current loan milestone."
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={selectedLoans.length > 1 ? false : ignoreDuplicates}
                                    onChange={(e) => setIgnoreDuplicates(e.target.checked)}
                                    disabled={selectedLoans.length > 1}
                                />
                            }
                            label="Ignore files that have already been uploaded.  Uncheck this if you are uploading to multiple loans, or if you have already uploaded the files before but want to send them again."
                        />

                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleConfirm} variant="contained" disabled={selectedLoans.length === 0}>Upload</Button>
            </DialogActions>
        </Dialog>
    );
}
